import { createContext, useContext, useState } from 'react';
import type { AgentDetailedResponse } from '@/shared/api/generated/api.schemas';
import { cn } from '@/shared/lib/utils/classnames';
import { Skeleton } from '@/shared/ui/skeleton';

interface AgentInfoContextValue {
  agent: AgentDetailedResponse;
}

const AgentInfoContext = createContext<AgentInfoContextValue | null>(null);

function useAgentInfoContext() {
  const context = useContext(AgentInfoContext);
  if (!context) {
    throw new Error('AgentInfo subcomponents must be used within AgentInfo');
  }
  return context;
}

interface AgentInfoProps {
  agent: AgentDetailedResponse;
  children: React.ReactNode;
  /** 컨테이너 클래스명 */
  className?: string;
}

/**
 * Agent 정보 컴포넌트 (Compound Component Pattern)
 *
 * @description
 * - Compound Component Pattern으로 각 하위 컴포넌트를 자유롭게 조합 가능
 * - 비즈니스 로직 없이 순수 UI 컴포넌트
 * - shared 레이어이므로 다른 위젯에서도 재사용 가능
 *
 * @example
 * ```tsx
 * // 기본 조합
 * <AgentInfo agent={agent}>
 *   <AgentInfo.Thumbnail />
 *   <AgentInfo.Name />
 *   <AgentInfo.Description />
 * </AgentInfo>
 *
 * // 커스텀 조합 (Creator를 별도 위치에)
 * <AgentInfo agent={agent}>
 *   <AgentInfo.Name />
 *   <AgentInfo.Creator className="ml-auto" />
 * </AgentInfo>
 *
 * // Description 줄 수 제어
 * <AgentInfo agent={agent}>
 *   <AgentInfo.Description maxLines={1} />
 * </AgentInfo>
 * ```
 */
export function AgentInfo({ agent, children, className }: AgentInfoProps) {
  return (
    <AgentInfoContext.Provider value={{ agent }}>
      <div className={cn(className)}>{children}</div>
    </AgentInfoContext.Provider>
  );
}

interface AgentInfoThumbnailProps {
  /** 이미지 로딩 전략 */
  loading?: 'eager' | 'lazy';
  /** 이미지 로딩 실패 시 처리 */
  onError?: () => void;
  /** 추가 클래스명 */
  className?: string;
}

/**
 * Agent 썸네일 이미지
 *
 * @description
 * - 고정 크기: 556px × 160px (레이아웃 쉬프트 방지)
 * - 이미지 로딩 전/실패 시 스켈레톤 표시
 * - 이미지가 없는 경우에도 스켈레톤 표시
 */
AgentInfo.Thumbnail = function AgentInfoThumbnail({
  loading = 'lazy',
  onError,
  className,
  ...props
}: AgentInfoThumbnailProps & React.ComponentProps<'img'>) {
  const { agent } = useAgentInfoContext();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // 실제 서버에서 제공하는 thumbnail URL 사용
  const thumbnailUrl = agent.thumbnail || `/images/agents/${agent.id}/${agent.id}_thumbnail.png`;
  const hasImage = !!agent.thumbnail;

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    onError?.();
  };

  // 이미지가 없거나 에러가 발생한 경우 스켈레톤 표시
  const showSkeleton = !hasImage || imageError || !imageLoaded;

  return (
    <div
      className={cn(
        'relative rounded-[8px] overflow-hidden w-full h-[160px] object-cover',
        className,
      )}
      {...props}
    >
      {/* 스켈레톤 (로딩 중 또는 이미지 없음 또는 에러) */}
      {showSkeleton && <Skeleton className="absolute inset-0 w-full h-full rounded-[8px]" />}

      {/* 이미지 (로딩 완료 시에만 표시) */}
      {hasImage && !imageError && (
        <img
          src={thumbnailUrl}
          alt={`${agent.name || 'Agent'} 배경`}
          loading={loading}
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
          className={cn(
            'object-cover w-full h-full transition-opacity duration-300',
            imageLoaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}

      {/* 서브타이틀 (이미지가 로드된 경우에만 표시) */}
      {agent.subTitle && imageLoaded && !imageError && (
        <div className="absolute inset-0 flex items-center left-6">
          <p
            className={cn('text-white whitespace-pre-line font-[800] leading-[21.6px] text-base')}
            style={{ textShadow: '0 1px 6px rgba(0, 0, 0, 0.25)' }}
          >
            {agent.subTitle}
          </p>
        </div>
      )}
    </div>
  );
};

interface AgentInfoNameProps {
  /** 추가 클래스명 */
  className?: string;
  /** HTML 태그 (기본: h3) */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
}

/**
 * Agent 이름
 */
AgentInfo.Name = function AgentInfoName({ className, as: Component = 'h3' }: AgentInfoNameProps) {
  const { agent } = useAgentInfoContext();

  return (
    <Component className={cn('title-medium-b text-black truncate', className)}>
      {agent.name || '이름 없음'}
    </Component>
  );
};

interface AgentInfoDescriptionProps {
  /** 최대 줄 수 (기본: 3줄, undefined 시 전체 표시) */
  maxLines?: number;
  /** 추가 클래스명 */
  className?: string;
}

/**
 * Agent Description
 *
 * @description
 * - maxLines로 줄 수 제어 가능 (1, 2, 3줄 등)
 * - undefined 시 전체 표시 (line-clamp 없음)
 * - 인라인 스타일로 동적 maxLines 지원
 */
AgentInfo.Description = function AgentInfoDescription({
  maxLines,
  className,
}: AgentInfoDescriptionProps) {
  const { agent } = useAgentInfoContext();

  if (!agent.description) {
    return null;
  }

  // Tailwind line-clamp 유틸리티는 동적 클래스 생성 불가하므로 인라인 스타일 사용
  // 일반적인 값들(1, 2, 3)은 Tailwind 클래스로, 그 외는 인라인 스타일로
  const lineClampClass =
    maxLines === 1
      ? 'line-clamp-1'
      : maxLines === 2
        ? 'line-clamp-2'
        : maxLines === 3
          ? 'line-clamp-3'
          : '';

  const inlineStyle =
    maxLines && !lineClampClass
      ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
      : undefined;

  return (
    <p
      className={cn('paragraph-medium text-label-weak', lineClampClass, className)}
      style={inlineStyle}
    >
      {agent.description}
    </p>
  );
};

interface AgentInfoCreatorProps {
  /** 추가 클래스명 */
  className?: string;
}

/**
 * Agent Creator (deprecated - 실제 데이터에 없음)
 * @deprecated 실제 서버 응답에 creator 필드가 없습니다.
 */
AgentInfo.Creator = function AgentInfoCreator({ className }: AgentInfoCreatorProps) {
  // const { agent } = useAgentInfoContext();

  return <span className={cn('label-small text-label-alternative', className)}>{'AX Labs'}</span>;
};
